import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "cn"

const alertVariants = cva(
  "relative w-full rounded-lg border border-neutrals-200 p-4 dark:border-neutrals-800 [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-neutrals-1000 dark:[&>svg]:text-neutrals-200 [&>svg+div]:translate-y-[-3px] [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default:
          "bg-white text-neutrals-1000 dark:bg-neutrals-1000 dark:text-neutrals-200",
        destructive:
          "border-primary-red-500/50 text-primary-red-500 dark:border-primary-red-500 dark:border-primary-red-700/50 dark:dark:border-primary-red-700 dark:text-primary-red-700 [&>svg]:text-primary-red-500 dark:[&>svg]:text-primary-red-700",
        success:
          "dark:bg-primary-green-950/50 border-primary-green-500/50 bg-primary-green-100/20 text-primary-green-600 *:data-[slot=alert-description]:text-primary-green-600/90 dark:text-primary-green-400 dark:*:data-[slot=alert-description]:text-primary-green-400/90 [&>svg]:text-primary-green-600 dark:[&>svg]:text-primary-green-400",
        info: "dark:bg-primary-green-950/50 border-primary-blue-300 bg-primary-blue-100 text-primary-blue-600 *:data-[slot=alert-description]:text-primary-blue-600 dark:text-primary-blue-300 [&>svg]:text-primary-blue-600 dark:[&>svg]:text-primary-blue-500",
        warning:
          "border-primary-orange-500 bg-primary-orange-100 *:data-[slot=alert-description]:text-neutrals-800 [&>svg]:text-primary-orange-500",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <h5
      data-slot="alert-title"
      className={cn(
        "mb-1 text-sm leading-none font-semibold tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-xs text-neutrals-800 [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-2 right-2", className)}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription, AlertAction }
